import React, { useState, useMemo } from "react";
import { toast } from "react-toastify";
import {
  FiAlertTriangle,
  FiInfo,
  FiLayers,
} from "react-icons/fi";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Collapse } from "@mui/material";
import { IoIosArrowDown } from "react-icons/io";
import BaseScopeAllocationModal from "../../../pages/ResourceEarning/fixedCost/BaseScopeAllocationModal";
import ChangeRequestAllocationModal from "../../../pages/ResourceEarning/fixedCost/ChangeRequestAllocationModal";
import { useTheme } from "../../../ThemeContext";
import { CustomNoRowsOverlay } from "../../common/OverlayGrid";

const FixedCostEarningsView = ({
  apiResources,
  apiSummary,
  apiInvoices = [],
  isAllocated = false,
  isGridLoading = false,
  dataGridSx,
}) => {
  const { isDarkMode } = useTheme();

  const [showBaseModal, setShowBaseModal] = useState(false);
  const [showCrModal, setShowCrModal] = useState(false);
  const [activeTab, setActiveTab] = useState("provisional");
  const [isNotesExpanded, setIsNotesExpanded] = useState(false);
  const [isProvisionalNotesExpanded, setIsProvisionalNotesExpanded] = useState(false);

  const handleAllocateCr = () => {
    setShowCrModal(true);
  };

  const handleConfirmCrRun = () => {
    toast.success("Change request run allocated successfully!");
    setShowCrModal(false);
  };

  const handleConfirmBaseRun = () => {
    toast.success("Base scope allocated successfully!");
    setShowBaseModal(false);
  };

  const allocationColumns = useMemo(
    () => [
      {
        field: "scope",
        headerName: "Scope",
        flex: 1.6,
        minWidth: 260,
        sortable: false,
        renderCell: (params) => {
          const run = params.row;
          return (
            <div className="d-flex align-items-center">
              <span
                className={`badge me-2 ${
                  isDarkMode
                    ? `bg-${run.tagType} bg-opacity-10 text-${run.tagType === "secondary" ? "light" : run.tagType} border border-${run.tagType} border-opacity-25`
                    : `bg-${run.tagType}-subtle text-${run.tagType} border`
                }`}
                style={{ fontSize: "11px", fontWeight: 600, padding: "3px 7px" }}
              >
                {run.scopeTag}
              </span>
              <span
                className={`fw-medium ${
                  run.isAbsorbed
                    ? isDarkMode ? "text-secondary" : "text-muted"
                    : isDarkMode ? "text-light" : "text-dark"
                }`}
                style={{ fontSize: "13px" }}
              >
                {run.scopeName}
              </span>
            </div>
          );
        },
      },
      {
        field: "hours",
        headerName: "Hours",
        flex: 0.8,
        minWidth: 90,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "13px", color: isDarkMode ? "#f1f5f9" : "#374151" }}>
            {params.value.toFixed(1)}
          </span>
        ),
      },
      {
        field: "cost",
        headerName: "Cost",
        flex: 1.0,
        minWidth: 110,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "13px", color: isDarkMode ? "#f1f5f9" : "#374151" }}>
            ₹{params.value.toLocaleString("en-IN")}
          </span>
        ),
      },
      {
        field: "received",
        headerName: "Received",
        flex: 1.1,
        minWidth: 120,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => {
          if (params.value === null) {
            return (
              <span className={`fst-italic ${isDarkMode ? "text-secondary" : "text-muted"}`} style={{ fontSize: "12px" }}>
                Awaiting inv.
              </span>
            );
          }
          if (params.value === 0) {
            return (
              <span className={isDarkMode ? "text-secondary" : "text-muted"} style={{ fontSize: "13px" }}>
                ₹0
              </span>
            );
          }
          return (
            <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "13px", color: isDarkMode ? "#f1f5f9" : "#374151" }}>
              ₹{params.value.toLocaleString("en-IN")}
            </span>
          );
        },
      },
      {
        field: "margin",
        headerName: "Margin",
        flex: 0.8,
        minWidth: 90,
        headerAlign: "center",
        align: "center",
        sortable: false,
        renderCell: (params) => {
          const val = params.value;
          if (val === "—") {
            return <span className={isDarkMode ? "text-secondary" : "text-muted"}>—</span>;
          }
          return (
            <span
              className="fw-bold"
              style={{
                color: isDarkMode ? "#34d399" : "#047857",
                fontVariantNumeric: "tabular-nums",
                fontSize: "13px",
              }}
            >
              {val}
            </span>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1.4,
        minWidth: 180,
        sortable: false,
        renderCell: (params) => {
          const run = params.row;
          const color = run.statusVariant;
          return (
            <span
              className={`badge d-inline-flex align-items-center gap-1 ${
                isDarkMode
                  ? `bg-${color} bg-opacity-10 text-${color === "light" ? "secondary" : color} border border-${color} border-opacity-25`
                  : `bg-${color}-subtle text-${color} border`
              }`}
              style={{ fontSize: "11.5px", fontWeight: 500, padding: "3px 10px" }}
            >
              <span className={`rounded-circle bg-${color}`} style={{ width: 6, height: 6 }} />
              {run.status}
            </span>
          );
        },
      },
      {
        field: "action",
        headerName: "Action",
        flex: 0.9,
        minWidth: 100,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => {
          const run = params.row;
          if (run.canAllocate) {
            return (
              <button
                type="button"
                className="re-btn-manual"
                style={{ height: "28px", padding: "0 14px", fontSize: "12px" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAllocateCr();
                }}
              >
                Allocate
              </button>
            );
          }
          return (
            <button
              className={`btn btn-sm border ${isDarkMode ? "btn-dark border-secondary text-secondary" : "btn-light text-muted"}`}
              disabled
              style={{ fontSize: "12px", padding: "3px 14px", cursor: "not-allowed" }}
            >
              Allocate
            </button>
          );
        },
      },
    ],
    [isDarkMode]
  );

  const provisionalColumns = useMemo(
    () => [
      {
        field: "resource",
        headerName: "Resource",
        flex: 1.6,
        minWidth: 240,
        sortable: false,
        renderCell: (params) => (
          <span className={`fw-medium ${isDarkMode ? "text-light" : "text-dark"}`} style={{ fontSize: "13px" }}>
            {params.value}
          </span>
        ),
      },
      {
        field: "hours",
        headerName: "Hours",
        flex: 0.8,
        minWidth: 80,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "13px", color: isDarkMode ? "#f1f5f9" : "#374151" }}>
            {params.value}
          </span>
        ),
      },
      {
        field: "rate",
        headerName: "Rate / hr",
        flex: 0.9,
        minWidth: 90,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "13px", color: isDarkMode ? "#f1f5f9" : "#374151" }}>
            ₹{params.value}
          </span>
        ),
      },
      {
        field: "cost",
        headerName: "Cost",
        flex: 1.0,
        minWidth: 100,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "13px", color: isDarkMode ? "#f1f5f9" : "#374151" }}>
            ₹{Number(params.value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        ),
      },
      {
        field: "sharePercent",
        headerName: "Share %",
        flex: 0.8,
        minWidth: 80,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span style={{ fontVariantNumeric: "tabular-nums", fontSize: "13px", color: isDarkMode ? "#f1f5f9" : "#374151" }}>
            {params.value}%
          </span>
        ),
      },
      {
        field: "earning",
        headerName: "Indicative Earning",
        flex: 1.2,
        minWidth: 130,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <span
            className="fw-bold"
            style={{
              color: isDarkMode ? "#34d399" : "rgb(4, 161, 137)",
              fontVariantNumeric: "tabular-nums",
              fontSize: "13px",
            }}
          >
            ₹{Number(params.value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        ),
      },
      {
        field: "margin",
        headerName: "Margin",
        flex: 1.1,
        minWidth: 120,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => {
          const num = Number(params.value || 0);
          const isPositive = num >= 0;
          return (
            <span
              className="fw-bold"
              style={{
                color: isPositive ? (isDarkMode ? "#38bdf8" : "#0284c7") : "#ef4444",
                fontVariantNumeric: "tabular-nums",
                fontSize: "13px",
              }}
            >
              ₹{num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.9,
        minWidth: 100,
        headerAlign: "center",
        align: "center",
        sortable: false,
        renderCell: (params) => {
          const isAlloc = params.value === "Allocated";
          return (
            <span
              className={`badge ${
                isAlloc
                  ? isDarkMode
                    ? "bg-success bg-opacity-10 text-success border border-success border-opacity-25"
                    : "bg-success-subtle text-success border"
                  : isDarkMode
                  ? "bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25"
                  : "bg-warning-subtle text-warning border"
              }`}
              style={{ fontSize: "11.5px", fontWeight: 500, padding: "3px 8px" }}
            >
              {params.value}
            </span>
          );
        },
      },
    ],
    [isDarkMode]
  );

  const provisionalRows = useMemo(() => {
    if (Array.isArray(apiResources) && apiResources.length > 0) {
      return apiResources.map((r, index) => {
        const hrs = parseFloat(r.working_hours || r.hours || 0);
        const costVal = Number(r.cost || 0);
        const effectiveRate =
          Number(r.rate_per_hours?.[0]?.per_hr_rate) ||
          Number(r.rate_per_hour) ||
          Number(r.rate) ||
          Number(r.per_hr_rate) ||
          (costVal && hrs ? Math.round(costVal / hrs) : 0);

        const shareVal =
          r.adjusted_share != null
            ? Number(r.adjusted_share)
            : r.computed_share != null
            ? Number(r.computed_share)
            : 0;

        const earningVal = Number(r.earning || 0);
        const marginVal =
          r.profit_margin != null
            ? Number(r.profit_margin)
            : earningVal - costVal;

        return {
          id: r.invoice_resource_id || r.user_id || `res-${index + 1}`,
          resource: r.name || r.user_name || `Resource ${index + 1}`,
          hours: hrs,
          rate: effectiveRate,
          cost: costVal,
          sharePercent: shareVal,
          earning: earningVal,
          margin: marginVal,
          status: isAllocated ? "Allocated" : "Provisional",
          raw: r,
        };
      });
    }
    return [];
  }, [apiResources, isAllocated]);

  const provisionalTotals = useMemo(() => {
    const totalHours = provisionalRows.reduce((acc, r) => acc + (Number(r.hours) || 0), 0);
    const totalCost =
      apiSummary?.total_cost != null && provisionalRows.length > 0
        ? Number(apiSummary.total_cost)
        : provisionalRows.reduce((acc, r) => acc + (Number(r.cost) || 0), 0);
    const totalEarning =
      apiSummary?.total_earning != null && provisionalRows.length > 0
        ? Number(apiSummary.total_earning)
        : provisionalRows.reduce((acc, r) => acc + (Number(r.earning) || 0), 0);
    const totalMargin =
      apiSummary?.total_profit != null && provisionalRows.length > 0
        ? Number(apiSummary.total_profit)
        : provisionalRows.reduce((acc, r) => acc + (Number(r.margin) || 0), 0);

    return {
      hours: totalHours.toFixed(1),
      cost: `₹${totalCost.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      share: provisionalRows.length > 0 ? "100%" : "0%",
      earning: `₹${totalEarning.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      margin: `₹${totalMargin.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    };
  }, [apiSummary, provisionalRows]);

  const allocationRuns = useMemo(() => {
    if (!apiSummary) return [];
    const totalRec = Number(apiSummary.total_received || 0);
    const totalCst = Number(apiSummary.total_cost || 0);
    const invoiceCount = Array.isArray(apiInvoices) ? apiInvoices.length : 0;
    return [
      {
        id: "base",
        scopeTag: "Base scope",
        scopeName: `Base scope (${invoiceCount} ${invoiceCount === 1 ? "receipt" : "receipts"})`,
        tagType: "secondary",
        hours: Number(provisionalTotals.hours || 0),
        cost: totalCst,
        received: totalRec,
        margin: totalCst > 0 ? `${(totalRec / totalCst).toFixed(2)}x` : "—",
        status: isAllocated ? "Allocated" : "Awaiting project completion",
        statusVariant: isAllocated ? "success" : "warning",
        canAllocate: !isAllocated && totalRec > 0,
        notes: isAllocated ? "Final allocation complete" : "Allocates when project complete",
      },
    ];
  }, [apiSummary, apiInvoices, isAllocated, provisionalTotals.hours]);

  const allocationTotals = useMemo(() => {
    const totalHours = allocationRuns.reduce((acc, r) => acc + (Number(r.hours) || 0), 0);
    const totalCost = allocationRuns.reduce((acc, r) => acc + (Number(r.cost) || 0), 0);
    const totalReceived = allocationRuns.reduce((acc, r) => acc + (Number(r.received) || 0), 0);
    const margin = totalCost > 0 ? `${(totalReceived / totalCost).toFixed(2)}x` : "—";
    return {
      hours: totalHours.toFixed(1),
      cost: `₹${totalCost.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      received: `₹${totalReceived.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      margin,
    };
  }, [allocationRuns]);

  return (
    <div className="fixed-cost-earnings-wrapper">
      {/* Sub-tabs / View Toggle */}
      <div
        className={`card border rounded shadow-sm p-2 px-3 mb-3 d-flex flex-row align-items-center justify-content-between flex-wrap gap-2 ${
          isDarkMode ? "bg-dark border-secondary" : "bg-white"
        }`}
      >
        <div className="d-flex align-items-center gap-2">
          {/* Provisional Resource Split button (First) */}
          <button
            type="button"
            className="btn btn-sm d-inline-flex align-items-center"
            style={{
              backgroundColor:
                activeTab === "provisional"
                  ? isDarkMode
                    ? "rgba(18, 183, 159, 0.2)"
                    : "rgba(18, 183, 159, 0.1)"
                  : "transparent",
              color:
                activeTab === "provisional"
                  ? isDarkMode
                    ? "#34d399"
                    : "rgba(18, 183, 159, 1)"
                  : isDarkMode
                  ? "#9ca3af"
                  : "#626161",
              border:
                activeTab === "provisional"
                  ? "1.5px solid rgba(18, 183, 159, 0.6)"
                  : isDarkMode
                  ? "1.5px solid #4b5563"
                  : "1.5px solid #d0d0d0",
              borderRadius: "4px",
              height: "34px",
              fontSize: "13px",
              fontWeight: activeTab === "provisional" ? 600 : 500,
              padding: "0 12px",
              gap: "6px",
              transition: "all 0.15s ease-in-out",
            }}
            onClick={() => setActiveTab("provisional")}
          >
            Provisional Resource Split
          </button>

          {/* Allocation Runs per Scope button (Second) */}
          <button
            type="button"
            className="btn btn-sm d-inline-flex align-items-center"
            style={{
              backgroundColor:
                activeTab === "runs"
                  ? isDarkMode
                    ? "rgba(18, 183, 159, 0.2)"
                    : "rgba(18, 183, 159, 0.1)"
                  : "transparent",
              color:
                activeTab === "runs"
                  ? isDarkMode
                    ? "#34d399"
                    : "rgba(18, 183, 159, 1)"
                  : isDarkMode
                  ? "#9ca3af"
                  : "#626161",
              border:
                activeTab === "runs"
                  ? "1.5px solid rgba(18, 183, 159, 0.6)"
                  : isDarkMode
                  ? "1.5px solid #4b5563"
                  : "1.5px solid #d0d0d0",
              borderRadius: "4px",
              height: "34px",
              fontSize: "13px",
              fontWeight: activeTab === "runs" ? 600 : 500,
              padding: "0 12px",
              gap: "6px",
              transition: "all 0.15s ease-in-out",
            }}
            onClick={() => setActiveTab("runs")}
          >
            <FiLayers size={16} />
            Allocation Runs per Scope
          </button>
        </div>

        {activeTab === "runs" && (
          <div className="d-flex gap-2">
            <button
              type="button"
              className="re-btn-manual"
              style={{ height: "31px", padding: "0 14px", fontSize: "13px" }}
              onClick={handleAllocateCr}
            >
              Allocate CTX-CR-014
            </button>
            <button
              className={`btn btn-sm ${isDarkMode ? "btn-outline-light" : "btn-outline-secondary"}`}
              style={{ fontSize: "13px" }}
              onClick={() => setShowBaseModal(true)}
            >
              Mark Project Complete &amp; Allocate
            </button>
          </div>
        )}
      </div>

          {/* Section A: Allocation Runs per Scope (MUI DataGrid) */}
          {activeTab === "runs" && (
        <div className={`card border rounded shadow-sm mb-4 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
          <div className={`card-header py-3 border-bottom d-flex align-items-center justify-content-between ${isDarkMode ? "bg-dark border-secondary text-light" : "bg-white"}`}>
            <div>
              <h6 className={`mb-0 fw-bold ${isDarkMode ? "text-light" : "text-dark"}`} style={{ fontSize: "14px" }}>
                Scope-wise Allocation Runs
              </h6>
              <span className={`small ${isDarkMode ? "text-secondary" : "text-muted"}`}>
                Base scope allocates at project completion; each approved change request allocates independently when billed.
              </span>
            </div>
            <span
              style={{
                backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.08)" : "#f1f5f9",
                color: isDarkMode ? "#cbd5e1" : "#475569",
                border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.15)" : "1px solid #e2e8f0",
                fontSize: "11.5px",
                fontWeight: 600,
                padding: "3px 9px",
                borderRadius: "9999px",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              2 of 3 Runs Active
            </span>
          </div>

          <Box sx={{ width: "100%", backgroundColor: isDarkMode ? "#1e1e1e" : "#fff" }}>
            <DataGrid
              rows={isGridLoading ? [] : allocationRuns}
              columns={allocationColumns}
              autoHeight
              loading={isGridLoading}
              hideFooterPagination
              disableRowSelectionOnClick
              getRowId={(row) => row.id}
              rowHeight={52}
              getRowClassName={(params) => (params.row.id === "cr-014" ? "active-run-row" : "")}
              slots={{
                noRowsOverlay: CustomNoRowsOverlay,
                footer: () => {
                  if (isGridLoading || allocationRuns.length === 0) return null;
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        borderTop: isDarkMode ? "1px solid #374151" : "1px solid #e0e0e0",
                        backgroundColor: isDarkMode ? "#262626" : "#f8fafc",
                        minHeight: "48px",
                        px: 2,
                        fontSize: "13px",
                        fontWeight: 700,
                        color: isDarkMode ? "#f1f5f9" : "#1e293b",
                      }}
                    >
                      <Box sx={{ flex: 1.6, minWidth: 260, pl: 1 }}>Project Total</Box>
                      <Box sx={{ flex: 0.8, minWidth: 90, textAlign: "right", pr: 2, fontVariantNumeric: "tabular-nums" }}>
                        {allocationTotals.hours}
                      </Box>
                      <Box sx={{ flex: 1.0, minWidth: 110, textAlign: "right", pr: 2, fontVariantNumeric: "tabular-nums" }}>
                        {allocationTotals.cost}
                      </Box>
                      <Box sx={{ flex: 1.1, minWidth: 120, textAlign: "right", pr: 2, fontVariantNumeric: "tabular-nums" }}>
                        {allocationTotals.received}
                      </Box>
                      <Box sx={{ flex: 0.8, minWidth: 90, textAlign: "center", color: isDarkMode ? "#34d399" : "#047857", fontVariantNumeric: "tabular-nums" }}>
                        {allocationTotals.margin}
                      </Box>
                      <Box sx={{ flex: 1.4, minWidth: 180 }}></Box>
                      <Box sx={{ flex: 0.9, minWidth: 100 }}></Box>
                    </Box>
                  );
                },
              }}
              sx={dataGridSx}
            />
          </Box>

          {/* Collapsible Guidelines / Notes Section */}
          <div
            className={`d-flex align-items-center justify-content-between py-2 px-3 border-top ${isDarkMode ? "bg-dark border-secondary text-light" : "bg-light"}`}
            style={{ cursor: "pointer", userSelect: "none" }}
            onClick={() => setIsNotesExpanded((prev) => !prev)}
          >
            <div className="d-flex align-items-center gap-2">
              <IoIosArrowDown
                size={18}
                style={{
                  color: "#04a189",
                  transition: "transform 0.25s ease-in-out",
                  transform: isNotesExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  flexShrink: 0,
                }}
              />
              <span className={`fw-semibold ${isDarkMode ? "text-light" : "text-dark"}`} style={{ fontSize: "12.5px" }}>
                Scope &amp; Allocation Guidelines
              </span>
              <span className={`small ${isDarkMode ? "text-secondary" : "text-muted"}`} style={{ fontSize: "12px" }}>
                ({isNotesExpanded ? "Click to collapse" : "Click to view why a run per scope & absorbed change requests"})
              </span>
            </div>
            <span className={`badge border ${isDarkMode ? "bg-dark text-secondary border-secondary" : "bg-white text-muted"}`} style={{ fontSize: "11px", fontWeight: 500 }}>
              {isNotesExpanded ? "Hide notes" : "2 notes available"}
            </span>
          </div>

          <Collapse in={isNotesExpanded} timeout={300}>
            <div className={`card-body border-top p-3 pt-2 ${isDarkMode ? "bg-dark border-secondary" : "bg-light"}`}>
              <div
                className={`d-flex align-items-center gap-2 mb-2 p-2 rounded ${
                  isDarkMode
                    ? "bg-info bg-opacity-10 text-info border border-info border-opacity-25"
                    : "alert alert-info border-0"
                }`}
                style={{ fontSize: "12.5px" }}
              >
                <FiInfo className="flex-shrink-0" size={16} />
                <div>
                  <strong>Why a run per scope:</strong> A change request settles when it is billed rather than when the whole project ends, so a long project stops showing months of nothing.
                </div>
              </div>
              <div
                className={`d-flex align-items-center gap-2 mb-0 p-2 rounded ${
                  isDarkMode
                    ? "bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25"
                    : "alert alert-warning border-0"
                }`}
                style={{ fontSize: "12.5px" }}
              >
                <FiAlertTriangle className="flex-shrink-0" size={16} />
                <div>
                  <strong>Absorbed change requests carry cost but no run:</strong> CTX-CR-013's 4 hours stay in cost and bring no earning, reflecting true absorbed write-offs.
                </div>
              </div>
            </div>
          </Collapse>
        </div>
      )}

      {/* Section B: Provisional Resource Split Table (MUI DataGrid) */}
      {activeTab === "provisional" && (
        <div className={`card border rounded shadow-sm mb-4 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
          <div className={`card-header py-3 border-bottom d-flex align-items-center justify-content-between ${isDarkMode ? "bg-dark border-secondary text-light" : "bg-white"}`}>
            <div>
              <h6 className={`mb-0 fw-bold ${isDarkMode ? "text-light" : "text-dark"}`} style={{ fontSize: "14px" }}>
                {isAllocated ? "Allocated Resource Split" : "Provisional Figures (Indicative Shares)"}
              </h6>
              <span className={`small ${isDarkMode ? "text-secondary" : "text-muted"}`}>
                {isAllocated
                  ? "Project is allocated. Final shares and earnings are written to resource records."
                  : "These are the shares the project would produce if it completed today. Not written to any resource record until final allocation."}
              </span>
            </div>
            {!isAllocated && (
              <button
                className={`btn btn-sm ${isDarkMode ? "btn-outline-light" : "btn-outline-secondary"}`}
                style={{ fontSize: "13px" }}
                onClick={() => setShowBaseModal(true)}
              >
                Mark project complete &amp; allocate
              </button>
            )}
          </div>

          <Box sx={{ width: "100%", backgroundColor: isDarkMode ? "#1e1e1e" : "#fff" }}>
            <DataGrid
              rows={isGridLoading ? [] : provisionalRows}
              columns={provisionalColumns}
              autoHeight
              loading={isGridLoading}
              hideFooterPagination
              disableRowSelectionOnClick
              getRowId={(row) => row.id}
              rowHeight={48}
              slots={{
                noRowsOverlay: CustomNoRowsOverlay,
                footer: () => {
                  if (isGridLoading || provisionalRows.length === 0) return null;
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        borderTop: isDarkMode ? "1px solid #374151" : "1px solid #e0e0e0",
                        backgroundColor: isDarkMode ? "#262626" : "#f8fafc",
                        minHeight: "48px",
                        px: 2,
                        fontSize: "13px",
                        fontWeight: 700,
                        color: isDarkMode ? "#f1f5f9" : "#1e293b",
                      }}
                    >
                      <Box sx={{ flex: 1.6, minWidth: 240, pl: 1 }}>Total</Box>
                      <Box sx={{ flex: 0.8, minWidth: 80, textAlign: "right", pr: 2, fontVariantNumeric: "tabular-nums" }}>
                        {provisionalTotals.hours}
                      </Box>
                      <Box sx={{ flex: 0.9, minWidth: 90 }}></Box>
                      <Box sx={{ flex: 1.0, minWidth: 100, textAlign: "right", pr: 2, fontVariantNumeric: "tabular-nums" }}>
                        {provisionalTotals.cost}
                      </Box>
                      <Box sx={{ flex: 0.8, minWidth: 80, textAlign: "right", pr: 2, fontVariantNumeric: "tabular-nums" }}>
                        {provisionalTotals.share}
                      </Box>
                      <Box sx={{ flex: 1.2, minWidth: 130, textAlign: "right", pr: 2, color: isDarkMode ? "#34d399" : "rgb(4, 161, 137)", fontVariantNumeric: "tabular-nums" }}>
                        {provisionalTotals.earning}
                      </Box>
                      <Box sx={{ flex: 1.1, minWidth: 120, textAlign: "right", pr: 2, color: isDarkMode ? "#38bdf8" : "#0284c7", fontVariantNumeric: "tabular-nums" }}>
                        {provisionalTotals.margin}
                      </Box>
                      <Box sx={{ flex: 0.9, minWidth: 100 }}></Box>
                    </Box>
                  );
                },
              }}
              sx={dataGridSx}
            />
          </Box>

          {/* Collapsible Guidelines / Notes Section */}
          <div
            className={`d-flex align-items-center justify-content-between py-2 px-3 border-top ${isDarkMode ? "bg-dark border-secondary text-light" : "bg-light"}`}
            style={{ cursor: "pointer", userSelect: "none" }}
            onClick={() => setIsProvisionalNotesExpanded((prev) => !prev)}
          >
            <div className="d-flex align-items-center gap-2">
              <IoIosArrowDown
                size={18}
                style={{
                  color: "#04a189",
                  transition: "transform 0.25s ease-in-out",
                  transform: isProvisionalNotesExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  flexShrink: 0,
                }}
              />
              <span className={`fw-semibold ${isDarkMode ? "text-light" : "text-dark"}`} style={{ fontSize: "12.5px" }}>
                Provisional Split Guidelines
              </span>
              <span className={`small ${isDarkMode ? "text-secondary" : "text-muted"}`} style={{ fontSize: "12px" }}>
                ({isProvisionalNotesExpanded ? "Click to collapse" : "Click to view project margin guidelines"})
              </span>
            </div>
            <span className={`badge border ${isDarkMode ? "bg-dark text-secondary border-secondary" : "bg-white text-muted"}`} style={{ fontSize: "11px", fontWeight: 500 }}>
              {isProvisionalNotesExpanded ? "Hide notes" : "1 note available"}
            </span>
          </div>

          <Collapse in={isProvisionalNotesExpanded} timeout={300}>
            <div className={`card-body border-top p-3 pt-2 ${isDarkMode ? "bg-dark border-secondary" : "bg-light"}`}>
              <div
                className={`d-flex align-items-center gap-2 mb-0 p-2 rounded ${
                  isDarkMode
                    ? "bg-info bg-opacity-10 text-info border border-info border-opacity-25"
                    : "alert alert-info border-0"
                }`}
                style={{ fontSize: "12.5px" }}
              >
                <FiInfo className="flex-shrink-0" size={16} />
                <div>
                  <strong>Project margin, not individual performance:</strong> Every resource on a fixed-cost project returns the same margin ratio unless the PM adjusts the shares manually before final allocation.
                </div>
              </div>
            </div>
          </Collapse>
        </div>
      )}

      {/* Modal: Base Scope Final Allocation */}
      <BaseScopeAllocationModal
        show={showBaseModal}
        onHide={() => setShowBaseModal(false)}
        onConfirm={handleConfirmBaseRun}
        splitData={provisionalRows}
      />

      {/* Modal: Change Request Allocation */}
      <ChangeRequestAllocationModal
        show={showCrModal}
        onHide={() => setShowCrModal(false)}
        onConfirm={handleConfirmCrRun}
        crData={[]}
      />
    </div>
  );
};

export default FixedCostEarningsView;
